import { useCallback, useEffect, useState } from 'react';
import { api, ApiError } from '../api/client';
import { useAuth } from '../state/auth';
import { Badge, Bandeau, Carte, Champ, Modale, Squelette, Vide } from '../components/ui';
import { dateHeureFr } from '../lib/format';

interface Demande {
  _id: string; entite: string; entiteId: string; libelleEntite: string; champ: string;
  valeurActuelle: string; valeurDemandee: string; motif: string; natureFinanciere: boolean;
  statut: string; createdAt: string; demandeur?: { nom: string }; traitePar?: { nom: string };
  commentaireTraitement?: string;
}

/**
 * La file d'arbitrage. Un agent bloque, c'est un client qui attend au comptoir :
 * le delai d'attente est affiche, et il passe en rouge au-dela de 15 minutes.
 */
export default function Corrections() {
  const { peut } = useAuth();
  const [d, setD] = useState<Demande[] | null>(null);
  const [choix, setChoix] = useState<{ demande: Demande; decision: 'accordee' | 'refusee' } | null>(null);
  const [commentaire, setCommentaire] = useState('');
  const [erreur, setErreur] = useState('');

  const charger = useCallback(() => { api.get<Demande[]>('/corrections').then(setD); }, []);
  useEffect(charger, [charger]);
  if (!d) return <Squelette lignes={6} />;

  const attente = d.filter((x) => x.statut === 'en_attente');

  async function trancher() {
    if (!choix) return;
    setErreur('');
    try {
      await api.post(`/corrections/${choix.demande._id}/traiter`, { decision: choix.decision, commentaire });
      setChoix(null); setCommentaire(''); charger();
    } catch (e) { setErreur(e instanceof ApiError ? e.erreur : 'Erreur'); }
  }

  const minutes = (iso: string) => Math.round((Date.now() - +new Date(iso)) / 60000);

  return (
    <>
      <div className="entete">
        <div className="titres">
          <h1>À valider</h1>
          <p>{attente.length} en attente · {peut('traiter.correction') ? 'vous pouvez trancher' : 'vos demandes'}</p>
        </div>
      </div>

      {attente.length === 0 ? <Vide titre="Rien en attente">Aucune demande de correction à traiter.</Vide> : (
        <div className="file">
          {attente.map((x) => {
            const m = minutes(x.createdAt);
            return (
              <div key={x._id} className="tache" style={{ borderLeftColor: m > 15 ? 'var(--danger)' : 'var(--attention)' }}>
                <div className="heure" style={{ fontSize: 16, color: m > 15 ? 'var(--danger)' : undefined }}>
                  {m < 60 ? `${m} min` : `${Math.round(m / 60)} h`}
                </div>
                <div className="corps">
                  <div className="micro">{x.demandeur?.nom ?? '—'} demande une correction</div>
                  <div className="qui">{x.libelleEntite} — champ « {x.champ} »</div>
                  <div className="quoi">
                    {x.valeurActuelle && x.valeurDemandee ? `${x.valeurActuelle} → ${x.valeurDemandee} · ` : ''}{x.motif}
                    {x.natureFinanciere && <span style={{ marginLeft: 6 }}><Badge statut="retard" libelle="Donnée financière" /></span>}
                  </div>
                </div>
                {peut('traiter.correction') ? (
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button className="btn btn-d" onClick={() => setChoix({ demande: x, decision: 'refusee' })}>Refuser</button>
                    <button className="btn btn-p" onClick={() => setChoix({ demande: x, decision: 'accordee' })}>Accorder</button>
                  </div>
                ) : <Badge statut="en_attente" libelle="En attente" />}
              </div>
            );
          })}
        </div>
      )}

      <div style={{ marginTop: 20 }}>
        <Carte titre="Historique" pad={false}>
          <table className="tbl compact">
            <thead><tr><th>Date</th><th>Demandeur</th><th>Dossier</th><th>Champ</th><th>Décision</th><th>Traite par</th></tr></thead>
            <tbody>
              {d.filter((x) => x.statut !== 'en_attente').map((x) => (
                <tr key={x._id}>
                  <td className="mini num">{dateHeureFr(x.createdAt)}</td>
                  <td>{x.demandeur?.nom ?? '—'}</td>
                  <td className="mini">{x.libelleEntite}</td>
                  <td className="mini sourdine">{x.champ}</td>
                  <td><Badge statut={x.statut === 'accordee' ? 'en_cours' : 'retard'} /></td>
                  <td className="mini">{x.traitePar?.nom ?? '—'}{x.commentaireTraitement && <div className="sourdine">{x.commentaireTraitement}</div>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Carte>
      </div>

      {choix && (
        <Modale titre={choix.decision === 'accordee' ? 'Accorder la correction' : 'Refuser la correction'} onFermer={() => setChoix(null)}>
          <Bandeau type="info" titre={choix.demande.libelleEntite}>
            {choix.demande.champ} — {choix.demande.motif}
          </Bandeau>
          <div style={{ marginTop: 12 }}>
            <Champ label="Commentaire" aide="Il sera conserve au journal, avec les deux valeurs.">
              <textarea value={commentaire} onChange={(e) => setCommentaire(e.target.value)} />
            </Champ>
          </div>
          {erreur && <div style={{ marginTop: 10 }}><Bandeau type="ko">{erreur}</Bandeau></div>}
          <div className="actions" style={{ marginTop: 14, justifyContent: 'flex-end' }}>
            <button className="btn" onClick={() => setChoix(null)}>Annuler</button>
            <button className={`btn ${choix.decision === 'accordee' ? 'btn-p' : 'btn-d'}`} onClick={() => void trancher()}>
              {choix.decision === 'accordee' ? 'Accorder' : 'Refuser'}
            </button>
          </div>
        </Modale>
      )}
    </>
  );
}
